/**
 * Source Registry — ProseMirror Plugin for late-binding citation numbers.
 *
 * Maintains an immutable plugin state that maps source UUIDs to display numbers
 * (1-based, in document encounter order). Updates incrementally on each transaction.
 *
 * Usage:
 *   - Import as a TipTap Extension and register with useEditor()
 *   - Read state via: sourceRegistryPluginKey.getState(editor.state)
 *   - The state provides: sourceMap (uuid → display number), activeSourceUuids (set)
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Extension } from '@tiptap/react'
import { computeVisibleSources, type HiddenByClaimUuid } from '@/shared/reviewedDoc'

// ── Plugin state type ────────────────────────────────────────────────────────

export interface SourceRegistryState {
  /** Map from source UUID → 1-based display number (encounter order) */
  sourceMap: Map<string, number>
  /** Set of all source UUIDs currently referenced in the document */
  activeSourceUuids: Set<string>
  /** Per-claim hidden-source map (keyed by claim_uuid). Citation nodes whose
   *  claim has any uuids in this set are filtered at compute time. */
  hiddenByClaimUuid: HiddenByClaimUuid
  /** Monotonically incrementing version — triggers NodeView updates via decorations */
  version: number
}

// ── Plugin key (use to read state from outside) ──────────────────────────────

export const sourceRegistryPluginKey = new PluginKey<SourceRegistryState>('sourceRegistry')

// ── Meta channel: dispatch hiddenByClaimUuid updates ─────────────────────────

/** Meta key on a transaction to push a new hidden-source map into the plugin. */
export const HIDDEN_SOURCES_META = 'sourceRegistry/hiddenByClaimUuid'

// ── State computation ────────────────────────────────────────────────────────

let _registryVersion = 0

function computeSourceState(
  doc: EditorState['doc'],
  hiddenByClaimUuid: HiddenByClaimUuid,
): SourceRegistryState {
  const sourceMap = new Map<string, number>()
  const activeSourceUuids = new Set<string>()
  let nextNum = 1

  doc.descendants((node) => {
    if (node.type.name === 'citation') {
      // Filter via computeVisibleSources — the same per-citation rule the
      // backend export filter applies (backend/core/export/review_filter.py),
      // so the live editor view and exported view stay in sync.
      const { visibleUuids } = computeVisibleSources(
        node.attrs as Parameters<typeof computeVisibleSources>[0],
        hiddenByClaimUuid,
      )

      for (const uuid of visibleUuids) {
        activeSourceUuids.add(uuid)
        if (!sourceMap.has(uuid)) {
          sourceMap.set(uuid, nextNum++)
        }
      }
    }
  })

  return { sourceMap, activeSourceUuids, hiddenByClaimUuid, version: ++_registryVersion }
}

// ── ProseMirror Plugin ───────────────────────────────────────────────────────

const sourceRegistryPMPlugin = new Plugin<SourceRegistryState>({
  key: sourceRegistryPluginKey,

  state: {
    init(_config, state) {
      return computeSourceState(state.doc, new Map())
    },

    apply(tr: Transaction, oldState: SourceRegistryState, _oldEditorState, newEditorState) {
      const meta = tr.getMeta(HIDDEN_SOURCES_META) as HiddenByClaimUuid | undefined
      // Recompute if either the doc changed OR the hidden-source map was pushed.
      // Both produce a new version, so NodeView decorations re-render.
      if (meta !== undefined) {
        return computeSourceState(newEditorState.doc, meta)
      }
      if (!tr.docChanged) return oldState
      return computeSourceState(newEditorState.doc, oldState.hiddenByClaimUuid)
    },
  },
})

// ── Citation Decoration Plugin ────────────────────────────────────────────────

export const citationDecoPluginKey = new PluginKey<DecorationSet>('citationDecorations')

function buildCitationDecos(state: EditorState): DecorationSet {
  const registry = sourceRegistryPluginKey.getState(state)
  if (!registry) return DecorationSet.empty
  const decos: Decoration[] = []
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'citation') {
      decos.push(Decoration.node(pos, pos + node.nodeSize, {}, {
        version: registry.version,
      }))
    }
  })
  return DecorationSet.create(state.doc, decos)
}

const citationDecoPMPlugin = new Plugin<DecorationSet>({
  key: citationDecoPluginKey,
  state: {
    init(_, state) { return buildCitationDecos(state) },
    apply(tr, old, _oldState, newState) {
      // Re-render citation decorations when either the doc changes OR the
      // hidden-source meta is dispatched (registry version bumped → labels
      // need to re-flow).
      if (!tr.docChanged && tr.getMeta(HIDDEN_SOURCES_META) === undefined) return old
      return buildCitationDecos(newState)
    },
  },
  props: {
    decorations(state) { return citationDecoPluginKey.getState(state) },
  },
})

// ── TipTap Extension wrapper ─────────────────────────────────────────────────

export const SourceRegistryExtension = Extension.create({
  name: 'sourceRegistry',

  addProseMirrorPlugins() {
    return [sourceRegistryPMPlugin, citationDecoPMPlugin]
  },
})

// ── Utility: get display numbers for a citation node's sourceUuids ───────────

/**
 * Given an editor state and a JSON string of source UUIDs, return the
 * display numbers from the Source Registry.
 */
export function getDisplayNumbers(
  state: EditorState,
  sourceUuidsJson: string,
): number[] {
  const registry = sourceRegistryPluginKey.getState(state)
  if (!registry) return []

  let uuids: string[] = []
  try {
    const parsed = JSON.parse(sourceUuidsJson)
    if (Array.isArray(parsed)) uuids = parsed
  } catch { /* empty */ }

  return uuids
    .map((uuid) => registry.sourceMap.get(uuid))
    .filter((n): n is number => n !== undefined)
    .sort((a, b) => a - b)
}
